import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemButton,
  Grid,
  Divider,
  Paper,
  Typography,
  Drawer,
  List,
  ListItemText,
  Fade,
  Chip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import logo from '../../assets/images/cards/CmdfwHeaderLogo.jpg';
import smalllogo from '../../assets/images/cards/Chinmaya-Mission-Logo.gif';
import { useMember } from '../../context/MemberProfileContext';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';


const drawerWidth = 280;

function HeaderBar({ drawerOpen, handleDrawerToggle }) {
  const { handleLogout, isLoggedIn } = useAuth();
  const { memberProfile, personName } = useMember();  
  const [anchorElRight, setAnchorElRight] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchBroadcastMessage();
  }, []);

  const fetchBroadcastMessage = async () => {
    const API_URL = process.env.REACT_APP_API_URL;
    try {
      const response = await fetch(`${API_URL}/broadcast`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setBroadcastMessage(data[0].message);
        } else {
          setErrorMessage("No broadcast message available.");
        }
      } else {
        setErrorMessage("Site is currently under maintenance. Please try again later");
      }
    } catch (error) {
      setErrorMessage("Site is currently under maintenance. Please try again later");
    }
  };
  
  const handleRightMenuOpen = (event) => {
    setAnchorElRight(event.currentTarget);
  };

  const handleRightMenuClose = () => {
    setAnchorElRight(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleDrawerToggle();
  };
  
  const onLogout = () => {
    handleLogout();
    navigate('/');
  };


  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
          width: '100%'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* Left side - Menu button */}
          {isLoggedIn && (
            <IconButton 
              color="inherit" 
              onClick={handleDrawerToggle}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Centered logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt="CMDFW Logo"
              sx={{
                maxHeight: { xs: '60px', sm: '80px' },
                width: 'auto',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            />
          </Box>

          {/* Right side - User info and logout */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {isLoggedIn && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IconButton 
                    color="inherit"
                    onClick={handleRightMenuOpen}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <PersonIcon />
                  </IconButton>
                  <Chip
                    label={personName}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
                <Menu
                  anchorEl={anchorElRight}
                  open={Boolean(anchorElRight)}
                  onClose={handleRightMenuClose}
                  TransitionComponent={Fade}
                  sx={{
                    '& .MuiPaper-root': {
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <MenuItem 
                    onClick={onLogout}
                    sx={{
                      gap: 1,
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <LogoutIcon fontSize="small" />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Broadcast Message Section */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: 0,
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          width: '100%',
          borderBottom: '1px solid #dee2e6'
        }}
      >
        <Box sx={{ 
          p: 3, 
          textAlign: 'center',
          maxWidth: 1000,
          mx: 'auto'
        }}>
          {broadcastMessage ? (
            <Fade in={true} timeout={1000}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#2c3e50',
                    mb: 2
                  }}
                >
                  📢 Balavihar Registration: {broadcastMessage}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 1,
                  flexWrap: 'wrap'
                }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#495057'
                    }}
                  >
                    📋 Read FAQ/How to Register document before you proceed:
                  </Typography>
                  <Box
                    component="a"
                    href="https://membership.cmdfw.org/docs/FAQ_How to Register.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)'
                      }
                    }}
                  >
                    📖 Open FAQ
                  </Box>
                </Box>
              </Box>
            </Fade>
          ) : (
            <Fade in={true} timeout={1000}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: '#e74c3c',
                  fontSize: '1.1rem'
                }}
              >
                ⚠️ {errorMessage}
              </Typography>
            </Fade>
          )}
        </Box>
      </Paper>

      {/* Navigation Drawer */}
      {isLoggedIn && (
        <Drawer
          anchor="left"
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? drawerOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
              borderRight: '1px solid #e9ecef',
              boxShadow: '4px 0 20px rgba(0,0,0,0.1)'
            },
          }}
        >
          {/* Logo Section */}
          <Box sx={{ 
            p: 3, 
            textAlign: 'center',
            borderBottom: '2px solid #e9ecef',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}>
            <Box
              component="img"
              src={smalllogo}
              alt="CMDFW Logo"
              sx={{
                maxWidth: '120px',
                height: 'auto',
                '&:hover': {
                  transform: 'scale(1.1)'
                }
              }}
            />
          </Box>

          <Divider sx={{ borderColor: '#e9ecef' }} />

          {/* Navigation Menu */}
          <List sx={{ px: 2, py: 1 }}>
            {memberProfile.menus && memberProfile.menus.map((item, index) => (
              <ListItemButton
                key={index}
                onClick={() => handleNavigation(item.uri)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  py: 1.5,
                  px: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transform: 'translateX(8px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                  },
                }}
              >
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }}
                />
              </ListItemButton>
            ))}
          </List>

          <Box sx={{ flexGrow: 1 }} />

          <Divider sx={{ borderColor: '#e9ecef' }} />

          {/* Logout Button */}
          <Box sx={{ p: 2 }}>
            <MenuItem
              onClick={onLogout}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 2,
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: 'white',
                fontWeight: 600,
                gap: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c0392b 0%, #a93226 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(231, 76, 60, 0.4)'
                },
              }}
            >
              <LogoutIcon fontSize="small" />
              Logout
            </MenuItem>
          </Box>
        </Drawer>
      )}
    </>
  );
}

export default HeaderBar;